/**
 * Header (C1) and mobile nav drawer (C2).
 *
 * Two custom elements plus a small shared drawer controller. The drawer
 * controller is exported on Velocity.drawers because C4 (cart) and C5 (search)
 * open the same way and must not each reimplement focus trapping and scroll
 * locking.
 */
(function () {
  'use strict';

  var V = window.Velocity;
  if (!V) return;

  /* =======================================================================
   * Shared drawer controller
   * ==================================================================== */
  var openDrawer = null;

  function overlay() {
    return document.querySelector('[data-drawer-overlay]');
  }

  function open(el, trigger) {
    if (!el || openDrawer === el) return;
    if (openDrawer) close({ restoreFocus: false });

    openDrawer = el;
    el.hidden = false;
    el._trigger = trigger || null;

    var ov = overlay();
    if (ov) ov.hidden = false;

    // Force a reflow so the browser registers the pre-transition transform
    // before .is-open flips it — without this the drawer would jump open
    // instead of sliding.
    void el.offsetWidth;

    el.classList.add('is-open');
    if (ov) ov.classList.add('is-open');

    V.lockScroll();
    // Pass the trigger explicitly so focus returns to it on close regardless
    // of what happened to have focus when open() was called.
    V.trapFocus(el, null, trigger || null);

    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    V.publish(V.EVENTS.drawerOpen, { drawer: el });
  }

  function close(options) {
    var el = openDrawer;
    if (!el) return;
    var opts = options || {};

    el.classList.remove('is-open');
    var ov = overlay();
    if (ov) ov.classList.remove('is-open');

    V.unlockScroll();
    V.releaseFocus(opts.restoreFocus !== false);

    if (el._trigger) el._trigger.setAttribute('aria-expanded', 'false');

    var done = function () {
      el.hidden = true;
      if (ov && !openDrawer) ov.hidden = true;
    };

    // Wait out the slide before hiding, unless motion is off.
    if (V.prefersReducedMotion()) {
      done();
    } else {
      setTimeout(done, 260);
    }

    openDrawer = null;
    V.publish(V.EVENTS.drawerClose, { drawer: el });
  }

  // Global handlers — registered once, not per drawer.
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      var target = document.getElementById(opener.getAttribute('data-drawer-open'));
      if (target) {
        e.preventDefault();
        open(target, opener);
      }
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-drawer-overlay]')) {
      e.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openDrawer) close();
  });

  V.drawers = { open: open, close: close, current: function () { return openDrawer; } };

  /* =======================================================================
   * C1 — sticky header with scroll-aware hide/reveal
   * ==================================================================== */
  class SiteHeader extends V.Element {
    init() {
      this.lastY = window.scrollY;
      this.cartCount = this.querySelector('[data-cart-count]');

      if (this.hasAttribute('data-hide-on-scroll')) {
        this.onScroll = V.throttleFrame(this.handleScroll.bind(this));
        this.on(window, 'scroll', this.onScroll, { passive: true });
      }

      this.initMegaMenus();

      // Keep the bubble in sync with any cart mutation from anywhere.
      this.listen(V.EVENTS.cartUpdate, this.updateCount.bind(this));
    }

    handleScroll() {
      var y = window.scrollY;
      var height = this.offsetHeight;

      // Never hide while a drawer is open — the close button lives up there.
      if (V.drawers.current()) {
        this.classList.remove('is-hidden');
        this.lastY = y;
        return;
      }

      // Ignore the elastic overscroll region at the top of the page.
      if (y <= height) {
        this.classList.remove('is-hidden');
        this.lastY = y;
        return;
      }

      var delta = y - this.lastY;
      // Small threshold so a jittery trackpad does not flicker the header.
      if (Math.abs(delta) < 6) return;

      this.classList.toggle('is-hidden', delta > 0);
      this.lastY = y;
    }

    updateCount(data) {
      if (!this.cartCount || !data) return;
      var count = typeof data.item_count === 'number' ? data.item_count : 0;
      this.cartCount.textContent = count;
      this.cartCount.classList.toggle('is-empty', count === 0);
    }

    /**
     * Mega menus. The markup works without JS (hover opens via CSS in older
     * approaches) — here we drive it explicitly so that keyboard and pointer
     * behave identically and Escape always closes.
     */
    initMegaMenus() {
      var self = this;
      this.megas = Array.prototype.slice.call(this.querySelectorAll('[data-megamenu]'));

      this.megas.forEach(function (item) {
        var toggle = item.querySelector('[aria-expanded]');
        var panel = item.querySelector('[data-megamenu-panel]');
        if (!toggle || !panel) return;

        var show = function () { self.setMega(item, true); };
        var hide = function () { self.setMega(item, false); };

        self.on(toggle, 'click', function (e) {
          e.preventDefault();
          self.setMega(item, toggle.getAttribute('aria-expanded') !== 'true');
        });

        // Pointer users get hover; keyboard users get focus. Both close on
        // leaving the whole item, which includes the panel.
        self.on(item, 'mouseenter', show);
        self.on(item, 'mouseleave', hide);
        self.on(item, 'focusin', show);
        self.on(item, 'focusout', function (e) {
          if (!item.contains(e.relatedTarget)) hide();
        });
      });

      this.on(document, 'keydown', function (e) {
        if (e.key !== 'Escape') return;
        var openItem = self.megas.filter(function (i) {
          return i.querySelector('[aria-expanded="true"]');
        })[0];
        if (!openItem) return;
        self.setMega(openItem, false);
        var toggle = openItem.querySelector('[aria-expanded]');
        if (toggle) toggle.focus();
      });
    }

    setMega(item, isOpen) {
      var toggle = item.querySelector('[aria-expanded]');
      var panel = item.querySelector('[data-megamenu-panel]');
      if (!toggle || !panel) return;

      // Only one panel open at a time.
      if (isOpen) {
        this.megas.forEach(function (other) {
          if (other === item) return;
          var t = other.querySelector('[aria-expanded]');
          var p = other.querySelector('[data-megamenu-panel]');
          if (t) t.setAttribute('aria-expanded', 'false');
          if (p) p.hidden = true;
        });
      }

      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      panel.hidden = !isOpen;
    }
  }

  /* =======================================================================
   * C2 — nav drawer
   *
   * Opening, focus trap, scroll lock and Escape are all handled by the shared
   * controller above. This element only owns behaviour specific to the nav:
   * closing when a link is followed, and collapsing accordions on close so the
   * drawer does not reopen in a half-expanded state.
   * ==================================================================== */
  class NavDrawer extends V.Element {
    init() {
      var self = this;

      this.on(this, 'click', function (e) {
        if (e.target.closest('a[href]')) V.drawers.close({ restoreFocus: false });
      });

      this.listen(V.EVENTS.drawerClose, function (data) {
        if (data.drawer !== self) return;
        Array.prototype.forEach.call(
          self.querySelectorAll('details[open]'),
          function (d) { d.open = false; }
        );
      });
    }
  }

  V.define('site-header', SiteHeader);
  V.define('nav-drawer', NavDrawer);
})();
